import { Award, Heart, Leaf, ShieldCheck, Users, BookOpen } from "lucide-react";
import { IMG } from "@/lib/site";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CtaSection from "@/components/CtaSection";

const vantaggi = [
  { icon: Award, title: "Esperienza nel settore", desc: "Allevamento riconosciuto ENCI da oltre 20 anni." },
  { icon: Heart, title: "Animali seguiti con attenzione", desc: "Cura quotidiana, alimentazione e controlli costanti." },
  { icon: Leaf, title: "Ambiente curato", desc: "Spazi puliti, sicuri e immersi nella natura." },
  { icon: BookOpen, title: "Consulenza prima e dopo", desc: "Supporto alle famiglie nella scelta e nell'inserimento." },
  { icon: ShieldCheck, title: "Salute al primo posto", desc: "Selezione attenta di morfologia, carattere e salute." },
  { icon: Users, title: "Passione cinofila", desc: "Nicola ed Elisa al fianco di ogni nuovo proprietario." },
];

export default function Allevamento() {
  return (
    <div data-testid="page-allevamento">
      <PageHero
        overline="Allevamento riconosciuto ENCI"
        title="Pastori tedeschi selezionati con passione"
        lead="Un luogo dove esperienza, professionalità e cura accompagnano ogni animale fin dai primi giorni di vita."
        image={IMG.hero}
        primaryCta={{ to: "/contatti", label: "Richiedi info cucciolate" }}
        secondaryCta={{ to: "/chi-siamo", label: "La nostra storia" }}
      />

      <section className="py-16 sm:py-24" data-testid="filosofia">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <SectionHeading overline="La nostra filosofia" title="Più di un'attività: una responsabilità." />
          </div>
          <div className="lg:col-span-7 reveal space-y-5 text-[#6B5F4A] leading-relaxed text-lg">
            <p>
              Per noi allevare non significa solo seguire una linea di sangue,
              ma prendersi cura di vite delicate, con attenzione, rispetto e una
              grande dose di pazienza. Ogni cane è seguito quotidianamente,
              ogni cucciolata viene preparata con consapevolezza.
            </p>
            <p>
              La nostra ricerca scrupolosa dei migliori riproduttori e dei
              migliori accoppiamenti ha un solo obiettivo: ottenere pastori
              tedeschi sani, equilibrati e fedeli allo standard di razza, con
              attenzione a <strong>salute, morfologia e carattere</strong>.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDD895]/60" data-testid="cura">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal order-2 lg:order-1">
            <SectionHeading
              overline="Cura e benessere"
              title="Ogni giornata costruita attorno al benessere dei nostri cani."
              lead="Ambiente, alimentazione, controlli, pulizia, socializzazione e attenzione quotidiana: ecco la routine che accompagna ogni nostro pastore tedesco."
            />
            <ul className="mt-7 space-y-3 text-[#6B5F4A]">
              {[
                "Alimentazione bilanciata e personalizzata per ogni fase",
                "Controlli sanitari periodici e profilassi accurata",
                "Spazi puliti, ampi e sereni",
                "Socializzazione costante con persone e altri cani",
                "Attenzione alla crescita equilibrata dei cuccioli",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#7A1F1F] flex-none" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 reveal order-1 lg:order-2">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden">
              <img src={IMG.cuccioli} alt="Cuccioli di pastore tedesco" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="cuccioli">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden">
              <img src={IMG.cristal} alt="Pastore tedesco adulto" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <SectionHeading
              overline="Cuccioli e famiglie"
              title="Scegliere un cucciolo è scegliere un compagno per la vita."
              lead="Accompagniamo ogni famiglia nel percorso di scelta e nell'inserimento del nuovo arrivato in casa, con consigli pratici e un confronto costante."
            />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDD895]/60" data-testid="vantaggi">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <SectionHeading
            overline="Perché scegliere il nostro allevamento"
            title="Sei buoni motivi per affidarti a noi."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {vantaggi.map((v, i) => (
              <div
                key={v.title}
                className="reveal bg-[#F5E6C0] border border-[#D9C48A] rounded-3xl p-7"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#EDD895] grid place-items-center mb-4 text-[#7A1F1F]">
                  <v.icon className="w-5 h-5" strokeWidth={1.4} />
                </div>
                <h4 className="font-sans font-semibold text-[#1A1710] text-lg">{v.title}</h4>
                <p className="mt-2 text-sm text-[#6B5F4A] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        title="Stai cercando un cucciolo di pastore tedesco?"
        lead="Scrivici per informazioni sulle cucciolate, sulle linee di sangue e sulla disponibilità attuale. Ti rispondiamo personalmente."
        primaryLabel="Richiedi informazioni"
      />
    </div>
  );
}
